import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  changePassword,
  getLanding,
  getMe,
  logOut,
  updateMe,
  type ChangePasswordFormValues,
  type MeResponse,
  type Permission,
} from '@groundly/shared';

import { call, isUnauthenticated } from './call.js';

/**
 * The current Account User, where they land, the website they last used and the
 * end of their session. Query keys follow the API path
 * (`patterns/frontend-conventions.md`, 1).
 */
export const meQueryKey = ['me'] as const;
export const landingQueryKey = ['me', 'landing'] as const;

/**
 * The session check. It runs on first load and after any `UNAUTHENTICATED`,
 * and is cached for the life of the page — the cache is cleared at log in and
 * at log out, which is what refreshes it.
 */
export function useMe() {
  return useQuery<MeResponse>({
    queryKey: meQueryKey,
    queryFn: () => call(getMe),
    staleTime: Infinity,
    gcTime: Infinity,
    // A refused check holds no data, so TanStack Query would otherwise re-run
    // it whenever the window regains focus or the connection returns. That
    // re-run puts the query back to pending, which unmounts the log in form
    // and loses what the Account User typed (`ux.md`, States: after a failed
    // attempt the email address and password stay). "Try again" and the next
    // mount repeat the check instead.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

/**
 * The permissions the nav and actions mirror (`patterns/frontend-conventions.md`,
 * 1). Never the only check: the API refuses what the Account User lacks.
 * Empty until the session check has answered.
 */
export function usePermissions(): readonly Permission[] {
  return useMe().data?.permissions ?? [];
}

/**
 * A fresh read of the current Account User, for a page that must not show what
 * was cached at log in (user settings `technical.md`, "Fresh profile on
 * open"): it fetches on every mount and keeps nothing once the page is left.
 *
 * It has its own key under `me`, so a failed read stays on the page that made
 * it — the session check's own query, which the frame depends on, keeps its
 * data and its state. A successful read also refreshes that query, so the
 * frame shows the same Account User the page does.
 */
export function useFreshMe() {
  const queryClient = useQueryClient();

  return useQuery<MeResponse>({
    queryKey: [...meQueryKey, 'fresh'],
    queryFn: async () => {
      const me = await call(getMe);
      queryClient.setQueryData(meQueryKey, me);
      return me;
    },
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    // Read once per visit: a background refetch that failed would take away a
    // form the Account User is typing into.
    refetchOnWindowFocus: false,
  });
}

/** Where a logged-in Account User lands. The server decides; the client maps. */
export function useLanding(enabled = true) {
  return useQuery({
    queryKey: landingQueryKey,
    queryFn: () => call(getLanding),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * Records the last website used. A failure never blocks the page unless it is
 * `404`, which the website-section layout turns into "Website not found".
 */
export function useRecordLastWebsite() {
  return useMutation({
    mutationFn: (websiteId: string) => call(updateMe, { body: { lastWebsiteId: websiteId } }),
  });
}

/**
 * Changes the Account User's own password. Nothing is invalidated on success:
 * the Account User and their data are unchanged, and the browser already holds
 * the new session's cookie.
 */
export function useChangePassword() {
  return useMutation({
    mutationFn: (values: ChangePasswordFormValues) => call(changePassword, { body: values }),
  });
}

export function useLogOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      try {
        await call(logOut);
      } catch (error) {
        // The session had already ended. The Account User is logged out
        // either way, so this is not a failure to report
        // (`flow.md`, E3). Every other failure is.
        if (!isUnauthenticated(error)) throw error;
      }
    },
    onSuccess: () => {
      // No page can show the previous Account User's data
      // (`patterns/frontend-conventions.md`, 1).
      queryClient.clear();
    },
  });
}
